import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import WhatsAppForm from '../components/WhatsAppForm';
import {
  Target,
  Globe,
  Shield,
  ArrowRight,
  CheckCircle,
  MessageCircle,
  Users,
  Tag,
  Rocket,
  BarChart3,
  TrendingUp,
  Zap,
  Phone,
  FileText,
  Settings,
  Eye,
  MousePointer,
  Search,
} from 'lucide-react';
import { images, contactByLanguage } from '../data/mock';

// Google Colors
const googleColors = {
  blue: '#4285F4',
  red: '#EA4335',
  yellow: '#FBBC04',
  green: '#34A853',
};

const ServicesPage = () => {
  const { language, t } = useLanguage();

  const currentContact = contactByLanguage[language] || contactByLanguage.es;

  const openWhatsApp = () => {
    window.open(`https://wa.me/${currentContact.whatsapp}`, '_blank');
  };

  const servicesDetailed = {
    es: [
      {
        id: 'campaigns',
        icon: <Target className="w-8 h-8" />,
        color: googleColors.blue,
        title: 'Creación y Optimización de Campañas Google Ads',
        subtitle: 'Campañas a medida para maximizar tu ROI',
        description: 'Diseñamos y gestionamos campañas Google Ads personalizadas para tu negocio. Desde la investigación de palabras clave hasta la optimización continua, nos encargamos de todo el proceso para que tú te centres en lo que mejor sabes hacer: dirigir tu empresa.',
        features: [
          { icon: <Search className="w-5 h-5" />, text: 'Investigación exhaustiva de palabras clave' },
          { icon: <FileText className="w-5 h-5" />, text: 'Redacción de anuncios persuasivos' },
          { icon: <Settings className="w-5 h-5" />, text: 'Configuración de campañas Search, Display, Performance Max' },
          { icon: <BarChart3 className="w-5 h-5" />, text: 'Estrategias de puja avanzadas y automatizadas' },
          { icon: <TrendingUp className="w-5 h-5" />, text: 'Optimización continua basada en datos' },
          { icon: <Eye className="w-5 h-5" />, text: 'Remarketing y audiencias personalizadas' },
        ],
        benefits: ['Aumento medio del 30% en conversiones', 'Reducción del coste por adquisición', 'Reporting semanal detallado', 'Sin permanencia'],
      },
      {
        id: 'leads',
        icon: <Users className="w-8 h-8" />,
        color: googleColors.red,
        title: 'Generación y Venta de Leads Cualificados',
        subtitle: 'Paga por resultados, no por clics',
        description: 'Generamos leads cualificados para tu empresa a través de formularios optimizados, Call Ads y landing pages de alta conversión. Nuestro modelo de pago por resultado te permite escalar sin riesgo: solo pagas por los leads que recibas.',
        features: [
          { icon: <Phone className="w-5 h-5" />, text: 'Call Ads para llamadas entrantes directas' },
          { icon: <FileText className="w-5 h-5" />, text: 'Formularios optimizados para conversión' },
          { icon: <MousePointer className="w-5 h-5" />, text: 'Landing pages de alta conversión' },
          { icon: <Target className="w-5 h-5" />, text: 'Segmentación precisa de audiencias' },
          { icon: <CheckCircle className="w-5 h-5" />, text: 'Verificación de calidad de leads' },
          { icon: <BarChart3 className="w-5 h-5" />, text: 'Tracking completo de conversiones' },
        ],
        benefits: ['Leads 100% cualificados', 'Pago por resultado', 'ROI >1.7', 'Sin inversión inicial'],
      },
      {
        id: 'compliance',
        icon: <Shield className="w-8 h-8" />,
        color: googleColors.green,
        title: 'Compliance y Resolución de Suspensiones',
        subtitle: 'Recupera tu cuenta Google Ads',
        description: 'Somos expertos en resolver problemas de compliance con Google Ads. Si tu cuenta ha sido suspendida, tus anuncios rechazados o necesitas completar verificaciones como BOV (Business Operations Verification), te ayudamos a recuperar el acceso y mantener tu cuenta segura.',
        features: [
          { icon: <Shield className="w-5 h-5" />, text: 'Resolución de suspensiones de cuenta' },
          { icon: <FileText className="w-5 h-5" />, text: 'Apelaciones BOV (Business Operations Verification)' },
          { icon: <CheckCircle className="w-5 h-5" />, text: 'Unacceptable Business Practices' },
          { icon: <Eye className="w-5 h-5" />, text: 'Verificación de anunciante' },
          { icon: <Settings className="w-5 h-5" />, text: 'Auditoría de políticas publicitarias' },
          { icon: <Target className="w-5 h-5" />, text: 'Prevención de futuras suspensiones' },
        ],
        benefits: ['Tasa de éxito superior al 85%', 'Respuesta rápida en 24-48h', 'Experiencia en sectores sensibles', 'Soporte continuo'],
      },
      {
        id: 'web',
        icon: <Globe className="w-8 h-8" />,
        color: googleColors.yellow,
        title: 'Creación de Sitios Web y Landing Pages',
        subtitle: 'Diseño orientado a la conversión',
        description: 'Diseñamos y desarrollamos sitios web y landing pages optimizados para convertir visitantes en clientes. Cada página está pensada para maximizar las conversiones, con una experiencia de usuario fluida y tiempos de carga ultrarrápidos.',
        features: [
          { icon: <Zap className="w-5 h-5" />, text: 'Velocidad de carga optimizada (<3s)' },
          { icon: <MousePointer className="w-5 h-5" />, text: 'Diseño CRO (Conversion Rate Optimization)' },
          { icon: <Globe className="w-5 h-5" />, text: 'Responsive design para todos los dispositivos' },
          { icon: <Search className="w-5 h-5" />, text: 'SEO técnico integrado' },
          { icon: <BarChart3 className="w-5 h-5" />, text: 'Integración con Google Analytics' },
          { icon: <CheckCircle className="w-5 h-5" />, text: 'A/B testing ready' },
        ],
        benefits: ['Aumento de tasa de conversión', 'Mobile-first design', 'Entrega en 2-4 semanas', 'Mantenimiento incluido'],
      },
      {
        id: 'tracking',
        icon: <Tag className="w-8 h-8" />,
        color: googleColors.blue,
        title: 'Tracking e Implementación de Conversiones',
        subtitle: 'Mide todo, mejora todo',
        description: 'Configuramos todo el ecosistema de tracking para que puedas medir cada conversión, cada llamada y cada formulario. Con Google Tag Manager, Google Analytics 4 y seguimiento de conversiones avanzado, tendrás visibilidad total sobre el rendimiento de tus campañas.',
        features: [
          { icon: <Tag className="w-5 h-5" />, text: 'Implementación de Google Tag Manager' },
          { icon: <BarChart3 className="w-5 h-5" />, text: 'Configuración de Google Analytics 4' },
          { icon: <Phone className="w-5 h-5" />, text: 'Tracking de llamadas y formularios' },
          { icon: <TrendingUp className="w-5 h-5" />, text: 'Conversiones offline import' },
          { icon: <Eye className="w-5 h-5" />, text: 'Enhanced conversions' },
          { icon: <FileText className="w-5 h-5" />, text: 'Reporting personalizado' },
        ],
        benefits: ['Datos precisos al 100%', 'Mejora de Smart Bidding', 'Attribution modeling', 'Dashboards en tiempo real'],
      },
    ],
    fr: [
      {
        id: 'campaigns',
        icon: <Target className="w-8 h-8" />,
        color: googleColors.blue,
        title: 'Création et Optimisation de Campagnes Google Ads',
        subtitle: 'Des campagnes sur mesure pour maximiser votre ROI',
        description: "Nous concevons et gérons des campagnes Google Ads personnalisées pour votre entreprise. De la recherche de mots-clés à l'optimisation continue, nous nous occupons de tout le processus pour que vous puissiez vous concentrer sur ce que vous faites de mieux : diriger votre entreprise.",
        features: [
          { icon: <Search className="w-5 h-5" />, text: 'Recherche exhaustive de mots-clés' },
          { icon: <FileText className="w-5 h-5" />, text: "Rédaction d'annonces persuasives" },
          { icon: <Settings className="w-5 h-5" />, text: 'Configuration campagnes Search, Display, Performance Max' },
          { icon: <BarChart3 className="w-5 h-5" />, text: "Stratégies d'enchères avancées et automatisées" },
          { icon: <TrendingUp className="w-5 h-5" />, text: 'Optimisation continue basée sur les données' },
          { icon: <Eye className="w-5 h-5" />, text: 'Remarketing et audiences personnalisées' },
        ],
        benefits: ['Augmentation moyenne de 30% des conversions', "Réduction du coût par acquisition", 'Reporting hebdomadaire détaillé', 'Sans engagement'],
      },
      {
        id: 'leads',
        icon: <Users className="w-8 h-8" />,
        color: googleColors.red,
        title: 'Génération et Vente de Leads Qualifiés',
        subtitle: 'Payez pour des résultats, pas des clics',
        description: "Nous générons des leads qualifiés pour votre entreprise via des formulaires optimisés, des Call Ads et des landing pages haute conversion. Notre modèle de paiement au résultat vous permet de scaler sans risque.",
        features: [
          { icon: <Phone className="w-5 h-5" />, text: 'Call Ads pour appels entrants directs' },
          { icon: <FileText className="w-5 h-5" />, text: 'Formulaires optimisés pour la conversion' },
          { icon: <MousePointer className="w-5 h-5" />, text: 'Landing pages haute conversion' },
          { icon: <Target className="w-5 h-5" />, text: "Segmentation précise d'audiences" },
          { icon: <CheckCircle className="w-5 h-5" />, text: 'Vérification qualité des leads' },
          { icon: <BarChart3 className="w-5 h-5" />, text: 'Tracking complet des conversions' },
        ],
        benefits: ['Leads 100% qualifiés', 'Paiement au résultat', 'ROI >1.7', 'Sans investissement initial'],
      },
      {
        id: 'compliance',
        icon: <Shield className="w-8 h-8" />,
        color: googleColors.green,
        title: 'Conformité et Résolution de Suspensions',
        subtitle: 'Récupérez votre compte Google Ads',
        description: "Nous sommes experts en résolution de problèmes de conformité Google Ads. Si votre compte a été suspendu ou si vous devez compléter des vérifications comme la BOV, nous vous aidons à récupérer l'accès.",
        features: [
          { icon: <Shield className="w-5 h-5" />, text: 'Résolution de suspensions de compte' },
          { icon: <FileText className="w-5 h-5" />, text: 'Appels BOV (Business Operations Verification)' },
          { icon: <CheckCircle className="w-5 h-5" />, text: 'Unacceptable Business Practices' },
          { icon: <Eye className="w-5 h-5" />, text: "Vérification d'annonceur" },
          { icon: <Settings className="w-5 h-5" />, text: 'Audit des politiques publicitaires' },
          { icon: <Target className="w-5 h-5" />, text: 'Prévention des futures suspensions' },
        ],
        benefits: ['Taux de succès supérieur à 85%', 'Réponse rapide en 24-48h', 'Expérience secteurs sensibles', 'Support continu'],
      },
      {
        id: 'web',
        icon: <Globe className="w-8 h-8" />,
        color: googleColors.yellow,
        title: 'Création de Sites Web et Landing Pages',
        subtitle: 'Design orienté conversion',
        description: "Nous concevons et développons des sites web et landing pages optimisés pour convertir les visiteurs en clients. Chaque page est pensée pour maximiser les conversions.",
        features: [
          { icon: <Zap className="w-5 h-5" />, text: 'Vitesse de chargement optimisée (<3s)' },
          { icon: <MousePointer className="w-5 h-5" />, text: 'Design CRO (Conversion Rate Optimization)' },
          { icon: <Globe className="w-5 h-5" />, text: 'Responsive design tous appareils' },
          { icon: <Search className="w-5 h-5" />, text: 'SEO technique intégré' },
          { icon: <BarChart3 className="w-5 h-5" />, text: 'Intégration Google Analytics' },
          { icon: <CheckCircle className="w-5 h-5" />, text: 'A/B testing ready' },
        ],
        benefits: ['Augmentation taux de conversion', 'Mobile-first design', 'Livraison en 2-4 semaines', 'Maintenance incluse'],
      },
      {
        id: 'tracking',
        icon: <Tag className="w-8 h-8" />,
        color: googleColors.blue,
        title: 'Tracking et Implémentation Conversions',
        subtitle: 'Mesurez tout, améliorez tout',
        description: "Nous configurons tout l'écosystème de tracking pour que vous puissiez mesurer chaque conversion. Avec Google Tag Manager et GA4, vous aurez une visibilité totale.",
        features: [
          { icon: <Tag className="w-5 h-5" />, text: 'Implémentation Google Tag Manager' },
          { icon: <BarChart3 className="w-5 h-5" />, text: 'Configuration Google Analytics 4' },
          { icon: <Phone className="w-5 h-5" />, text: 'Tracking appels et formulaires' },
          { icon: <TrendingUp className="w-5 h-5" />, text: 'Import conversions offline' },
          { icon: <Eye className="w-5 h-5" />, text: 'Enhanced conversions' },
          { icon: <FileText className="w-5 h-5" />, text: 'Reporting personnalisé' },
        ],
        benefits: ['Données précises à 100%', 'Amélioration Smart Bidding', 'Attribution modeling', 'Dashboards temps réel'],
      },
    ],
    en: [
      {
        id: 'campaigns',
        icon: <Target className="w-8 h-8" />,
        color: googleColors.blue,
        title: 'Google Ads Campaign Creation & Optimization',
        subtitle: 'Custom campaigns to maximize your ROI',
        description: 'We design and manage personalized Google Ads campaigns for your business. From keyword research to continuous optimization, we handle the entire process so you can focus on what you do best: running your company.',
        features: [
          { icon: <Search className="w-5 h-5" />, text: 'Exhaustive keyword research' },
          { icon: <FileText className="w-5 h-5" />, text: 'Persuasive ad copywriting' },
          { icon: <Settings className="w-5 h-5" />, text: 'Search, Display, Performance Max setup' },
          { icon: <BarChart3 className="w-5 h-5" />, text: 'Advanced automated bid strategies' },
          { icon: <TrendingUp className="w-5 h-5" />, text: 'Data-driven continuous optimization' },
          { icon: <Eye className="w-5 h-5" />, text: 'Remarketing and custom audiences' },
        ],
        benefits: ['Average 30% increase in conversions', 'Reduced cost per acquisition', 'Detailed weekly reporting', 'No commitment'],
      },
      {
        id: 'leads',
        icon: <Users className="w-8 h-8" />,
        color: googleColors.red,
        title: 'Qualified Lead Generation & Sales',
        subtitle: 'Pay for results, not clicks',
        description: 'We generate qualified leads for your business through optimized forms, Call Ads, and high-converting landing pages. Our pay-per-result model lets you scale risk-free.',
        features: [
          { icon: <Phone className="w-5 h-5" />, text: 'Call Ads for direct incoming calls' },
          { icon: <FileText className="w-5 h-5" />, text: 'Conversion-optimized forms' },
          { icon: <MousePointer className="w-5 h-5" />, text: 'High-converting landing pages' },
          { icon: <Target className="w-5 h-5" />, text: 'Precise audience targeting' },
          { icon: <CheckCircle className="w-5 h-5" />, text: 'Lead quality verification' },
          { icon: <BarChart3 className="w-5 h-5" />, text: 'Complete conversion tracking' },
        ],
        benefits: ['100% qualified leads', 'Pay per result', 'Guaranteed ROI >1.7', 'No upfront investment'],
      },
      {
        id: 'compliance',
        icon: <Shield className="w-8 h-8" />,
        color: googleColors.green,
        title: 'Compliance & Suspension Resolution',
        subtitle: 'Recover your Google Ads account',
        description: 'We are experts in resolving Google Ads compliance issues. If your account has been suspended or you need to complete verifications like BOV, we help you regain access.',
        features: [
          { icon: <Shield className="w-5 h-5" />, text: 'Account suspension resolution' },
          { icon: <FileText className="w-5 h-5" />, text: 'BOV (Business Operations Verification) appeals' },
          { icon: <CheckCircle className="w-5 h-5" />, text: 'Unacceptable Business Practices' },
          { icon: <Eye className="w-5 h-5" />, text: 'Advertiser verification' },
          { icon: <Settings className="w-5 h-5" />, text: 'Advertising policy audit' },
          { icon: <Target className="w-5 h-5" />, text: 'Future suspension prevention' },
        ],
        benefits: ['Success rate above 85%', 'Fast 24-48h response', 'Sensitive sector experience', 'Ongoing support'],
      },
      {
        id: 'web',
        icon: <Globe className="w-8 h-8" />,
        color: googleColors.yellow,
        title: 'Website & Landing Page Creation',
        subtitle: 'Conversion-focused design',
        description: 'We design and develop websites and landing pages optimized to convert visitors into customers. Every page is designed to maximize conversions.',
        features: [
          { icon: <Zap className="w-5 h-5" />, text: 'Optimized load speed (<3s)' },
          { icon: <MousePointer className="w-5 h-5" />, text: 'CRO design (Conversion Rate Optimization)' },
          { icon: <Globe className="w-5 h-5" />, text: 'Responsive design all devices' },
          { icon: <Search className="w-5 h-5" />, text: 'Integrated technical SEO' },
          { icon: <BarChart3 className="w-5 h-5" />, text: 'Google Analytics integration' },
          { icon: <CheckCircle className="w-5 h-5" />, text: 'A/B testing ready' },
        ],
        benefits: ['Increased conversion rate', 'Mobile-first design', 'Delivery in 2-4 weeks', 'Maintenance included'],
      },
      {
        id: 'tracking',
        icon: <Tag className="w-8 h-8" />,
        color: googleColors.blue,
        title: 'Tracking & Conversion Implementation',
        subtitle: 'Measure everything, improve everything',
        description: 'We set up the entire tracking ecosystem so you can measure every conversion. With GTM and GA4, you get complete visibility.',
        features: [
          { icon: <Tag className="w-5 h-5" />, text: 'Google Tag Manager implementation' },
          { icon: <BarChart3 className="w-5 h-5" />, text: 'Google Analytics 4 configuration' },
          { icon: <Phone className="w-5 h-5" />, text: 'Call and form tracking' },
          { icon: <TrendingUp className="w-5 h-5" />, text: 'Offline conversion import' },
          { icon: <Eye className="w-5 h-5" />, text: 'Enhanced conversions' },
          { icon: <FileText className="w-5 h-5" />, text: 'Custom reporting' },
        ],
        benefits: ['100% accurate data', 'Improved Smart Bidding', 'Attribution modeling', 'Real-time dashboards'],
      },
    ],
  };

  const services = servicesDetailed[language] || servicesDetailed.es;

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-white shadow-md px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <img src={images.googleAdsLogo} alt="Google Ads" className="h-5 w-auto" />
                <span className="text-slate-700">{t.services.title}</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                {t.services.title}
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                {t.services.subtitle}
              </p>
              <Link to={`/${language}/contacto`}>
                <Button size="lg" className="text-white" style={{ backgroundColor: googleColors.blue }}>
                  {t.hero.cta}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div>
              <img src={images.digitalMarketing} alt="Digital Marketing" className="w-full h-auto rounded-2xl shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div
                key={service.id}
                id={service.id}
                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? '' : ''}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg"
                    style={{ backgroundColor: service.color }}
                  >
                    {service.icon}
                  </div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-2">{service.title}</h2>
                  <p className="text-lg font-medium mb-4" style={{ color: service.color }}>{service.subtitle}</p>
                  <p className="text-slate-600 text-lg leading-relaxed mb-8">{service.description}</p>
                  
                  <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-start space-x-3 p-3 bg-slate-50 rounded-xl">
                        <div className="text-slate-500">{feature.icon}</div>
                        <span className="text-sm text-slate-700">{feature.text}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {service.benefits.map((benefit, bIndex) => (
                      <span 
                        key={bIndex}
                        className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium text-white"
                        style={{ backgroundColor: service.color }}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <Card className="bg-slate-50 border-0 shadow-xl rounded-2xl overflow-hidden">
                    <div className="h-2" style={{ backgroundColor: service.color }} />
                    <CardContent className="p-8">
                      <img 
                        src={index === 0 ? images.analytics : index === 1 ? images.teamWork : index === 2 ? images.dashboard : images.growth} 
                        alt={service.title}
                        className="w-full h-64 object-cover rounded-xl mb-6"
                      />
                      <div className="text-center">
                        <Link to={`/${language}/contacto`}>
                          <Button className="text-white" style={{ backgroundColor: service.color }}>
                            {t.cta.button}
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: `linear-gradient(135deg, ${googleColors.blue} 0%, #1a73e8 100%)` }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t.cta.title}
          </h2>
          <p className="text-blue-100 text-lg mb-8">{t.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/${language}/contacto`}>
              <Button size="lg" className="bg-white hover:bg-slate-50 px-8" style={{ color: googleColors.blue }}>
                {t.cta.button}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button
              variant="outline"
              size="lg"
              onClick={openWhatsApp}
              className="border-white/50 text-white hover:bg-white/10 px-8"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
